import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Fields match `views::segments` on the backend exactly.
//
// Two things the API keeps apart, and so does this: what somebody DECLARED
// (`members`) and what that currently RESOLVES to (`resolved`, `addressCount`,
// `unresolved`). Membership is authored once and re-resolved against live
// inventory, so the second changes without anyone editing the first — a VLAN
// gains a host, an endpoint ages out. A reader has to be able to tell which
// they are looking at.

export type MemberKind = 'cidr' | 'endpoint' | 'vlan' | 'interface';

export interface MemberRule {
  id: string;
  kind: MemberKind;
  value: string;
  /** Excludes rather than includes, so a hole needs no second segment. */
  negated: boolean;
  /** "VLAN 20 (3 endpoints)" — why a host is a member, without leaving the page. */
  detail: string | null;
}

export interface UnresolvedMember {
  ruleId: string;
  kind: string;
  value: string;
  reason: string;
}

export interface Segment {
  id: string;
  regionId: string;
  name: string;
  description: string | null;
  members: MemberRule[];
  resolved: string[];
  addressCount: number;
  /** Rules naming something we could not find. Never silently dropped. */
  unresolved: UnresolvedMember[];
}

export interface MemberInput {
  kind: MemberKind;
  value: string;
  negated: boolean;
}

export interface CreateSegmentRequest {
  regionId: string;
  name: string;
  description?: string | null;
  members: MemberInput[];
}

export interface UpdateSegmentRequest {
  name?: string;
  description?: string | null;
  /** Omit to leave membership alone; send to replace it wholesale. */
  members?: MemberInput[];
}

export function useSegments() {
  const contextReady = useActiveContextReady();
  return useQuery<Segment[]>({
    queryKey: ['segments'],
    queryFn: async () => {
      const response = await axios.get<Segment[]>(`${config.backendApi}/api/segments`, {
        withCredentials: true,
      });
      return response.data;
    },
    enabled: contextReady,
  });
}

export function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSegmentRequest) => {
      const response = await axios.post<Segment>(`${config.backendApi}/api/segments`, data, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      // A changed segment changes every matrix cell that mentions it.
      void queryClient.invalidateQueries({ queryKey: ['segmentationMatrix'] });
    },
  });
}

export function useUpdateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateSegmentRequest & { id: string }) => {
      const response = await axios.patch<Segment>(`${config.backendApi}/api/segments/${id}`, data, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      void queryClient.invalidateQueries({ queryKey: ['segmentationMatrix'] });
    },
  });
}

export function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.backendApi}/api/segments/${id}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      void queryClient.invalidateQueries({ queryKey: ['segmentationMatrix'] });
    },
  });
}

// Importing a UniFi console's firewall zones as segments. Fields match
// `views::segments::{ConsoleImport, ZoneImport}`.

export type ZoneImportAction = 'create' | 'update' | 'unchanged' | 'remove' | 'blocked';

export interface ZoneImport {
  zoneId: string;
  /** The zone's name in UniFi; null for a zone the console no longer has. */
  zone: string | null;
  action: ZoneImportAction;
  segmentId: string | null;
  segmentName: string;
  /** The segment's members afterwards, each a subnet. */
  members: string[];
  added: string[];
  /** Includes members added by hand to a segment an import made. */
  removed: string[];
  /** Networks that give no member, each with why. */
  leftOut: string[];
  /** Why a blocked zone cannot be imported. */
  reason: string | null;
}

export interface ConsoleImport {
  deviceId: string;
  deviceName: string;
  regionId: string;
  regionName: string;
  /** Applying names it, so a newer snapshot is reviewed before it is imported. */
  snapshotId: string;
  capturedAt: string;
  zones: ZoneImport[];
}

export function useUnifiImports() {
  const contextReady = useActiveContextReady();
  return useQuery<ConsoleImport[]>({
    queryKey: ['segmentImports'],
    queryFn: async () => {
      const response = await axios.get<ConsoleImport[]>(`${config.backendApi}/api/segments/unifi-import`, {
        withCredentials: true,
      });
      return response.data;
    },
    enabled: contextReady,
  });
}

export function useApplyUnifiImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { deviceId: string; snapshotId: string }) => {
      const response = await axios.post<ConsoleImport>(`${config.backendApi}/api/segments/unifi-import`, data, {
        withCredentials: true,
      });
      return response.data;
    },
    // Settled, not success: a 409 means a newer snapshot arrived, and the
    // preview it asks to be reviewed is the refetched one.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['segmentImports'] });
      void queryClient.invalidateQueries({ queryKey: ['segments'] });
      void queryClient.invalidateQueries({ queryKey: ['segmentationMatrix'] });
    },
  });
}
